import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import {
  parseOnHand,
  parseReorderLevel,
  stockItemTitle,
  stockItemVariantLabel
} from "@/lib/stock-item";
import type { StoreStockTrackingItem } from "@repo/api-sdk";
import { TruncateTooltip } from "@repo/design-system";
import { ImageOff, Trash2 } from "lucide-react";
import { memo, useEffect, useState, type KeyboardEvent } from "react";

export interface StockItemSave {
  reorder_level: number;
  on_hand: number | null;
}

interface StockItemRowProps {
  item: StoreStockTrackingItem;
  /** Persist a full-state edit (both fields) - only fires when a value changed. */
  onSave: (variantId: string, next: StockItemSave) => void;
  onRemove: (variantId: string, label: string) => void;
  disabled?: boolean;
}

/**
 * One tracked item. Holds local input state for `reorder_level` + `on_hand` and
 * commits a full-state upsert on blur / Enter (the API upsert is full-state, so
 * both fields are always sent together). Renders a blank `on_hand` as
 * "Not counted" - never `0` - and flags the row when it is due to reorder.
 */
export const StockItemRow = memo(function StockItemRow({
  item,
  onSave,
  onRemove,
  disabled = false
}: StockItemRowProps) {
  const [level, setLevel] = useState(String(item.reorder_level));
  const [onHand, setOnHand] = useState(
    item.on_hand === null ? "" : String(item.on_hand)
  );

  // Re-sync from the server (e.g. after a refetch reconciles the optimistic
  // edit). Values agree with what was typed, so this doesn't fight the user.
  useEffect(() => {
    setLevel(String(item.reorder_level));
    setOnHand(item.on_hand === null ? "" : String(item.on_hand));
  }, [item.reorder_level, item.on_hand]);

  const commit = () => {
    const reorder_level = parseReorderLevel(level, item.reorder_level);
    const on_hand = parseOnHand(onHand, item.on_hand);

    // Reflect the normalized values back into the inputs.
    setLevel(String(reorder_level));
    setOnHand(on_hand === null ? "" : String(on_hand));

    if (reorder_level !== item.reorder_level || on_hand !== item.on_hand) {
      onSave(item.variant_id, { reorder_level, on_hand });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };

  const title = stockItemTitle(item);
  const thumbnail = item.variant?.product?.thumbnail ?? null;
  const isUnavailable = item.variant === null;
  // The variant identity, shown as a pill - unless the variant title is already
  // the primary line (no product title) or just duplicates it. This is what
  // makes "which variant am I tracking" unambiguous for multi-variant products
  // (e.g. shade / size).
  const variantLabel = stockItemVariantLabel(item);
  const sku = item.variant?.sku ?? null;
  const rowLabel = variantLabel ? `${title} (${variantLabel})` : title;

  return (
    <div className="border-border flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <div className="border-border flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-gray-50 dark:bg-neutral-900">
          <ProductThumbnailImage
            src={thumbnail}
            alt=""
            className="h-full w-full object-cover"
            fallback={
              <ImageOff
                className="h-5 w-5 text-gray-300 dark:text-gray-600"
                aria-hidden="true"
              />
            }
          />
        </div>
        <div className="min-w-0">
          <TruncateTooltip
            as="p"
            className="font-medium text-gray-900 dark:text-white"
          >
            {title}
          </TruncateTooltip>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
            {isUnavailable ? (
              <span>No longer available from any supplier</span>
            ) : (
              <>
                {variantLabel ? (
                  <span className="border-border inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
                    {variantLabel}
                  </span>
                ) : null}
                {sku ? <span className="text-xs">SKU {sku}</span> : null}
                {!variantLabel && !sku ? (
                  <span className="text-xs text-gray-400">
                    Variant {item.variant_id.slice(-8)}
                  </span>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 sm:justify-end">
        <label className="flex flex-col text-xs font-medium text-gray-500 dark:text-gray-400">
          On hand
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={onHand}
            placeholder="—"
            aria-label={`On hand count for ${rowLabel}`}
            disabled={disabled}
            onChange={(event) => setOnHand(event.target.value)}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            className="mt-1 h-9 w-20"
          />
        </label>

        <label className="flex flex-col text-xs font-medium text-gray-500 dark:text-gray-400">
          Reorder at
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={level}
            aria-label={`Reorder level for ${rowLabel}`}
            disabled={disabled}
            onChange={(event) => setLevel(event.target.value)}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            className="mt-1 h-9 w-20"
          />
        </label>

        <div className="flex w-28 flex-shrink-0 items-center justify-end">
          {item.is_due ? (
            <Badge variant="warning">Due to reorder</Badge>
          ) : item.on_hand === null ? (
            <span className="text-xs text-gray-400">Not counted</span>
          ) : (
            <Badge variant="success">In stock</Badge>
          )}
        </div>

        <button
          type="button"
          onClick={() => onRemove(item.variant_id, rowLabel)}
          disabled={disabled}
          aria-label={`Stop tracking ${rowLabel}`}
          className="flex-shrink-0 rounded-md p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-red-900/20"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
});
